// Shared helpers for anything that renders or compares money from the
// billing API.
//
// **Ported from `front-porch/src/utils/billingHelpers.ts`.** That module
// is the reference implementation and carries the full set (aging
// checks, PDF saving, idempotency keys, error extraction). This is the
// subset the member portal needs today; when the homeowner ledger lands
// here, extend this file from that one rather than starting a third.

import { AgingBuckets, FineReturn } from '../clients/rust-api-client';
import { apiErrorMessage } from './apiErrors';

/**
 * Format a decimal string from the API as US currency.
 *
 * **Money is a string and must never become a number.** Every amount the
 * billing API returns is an exact decimal — `"1250.00"` — and
 * `parseFloat` is the one step that can silently change it. So this
 * groups the integer part with a regex and copies the cents through
 * untouched; nothing here does arithmetic.
 *
 * The sign goes in front of the currency mark (`-$40.00`), which is the
 * form a homeowner reads without stumbling.
 */
export const formatMoney = (amount?: string | null): string => {
  if (amount === null || amount === undefined || amount === '') return '-';

  const negative = amount.trim().startsWith('-');
  const digits = amount.trim().replace(/^[-+]/, '');
  const [whole, cents = '00'] = digits.split('.');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  return `${negative ? '-' : ''}$${grouped}.${cents.padEnd(2, '0').slice(0, 2)}`;
};

const MONEY_PATTERN = /^[-+]?\d+(\.\d+)?$/;

// `0n` and friends are the natural way to write these, and they do not
// compile: this project targets ES5, where TypeScript rejects a BigInt
// literal outright (TS2737). `BigInt(0)` typechecks and behaves
// identically, so leave the calls alone rather than tidying them.
const ZERO = BigInt(0);
const HUNDRED = BigInt(100);

/**
 * Exact cents for a decimal money string, as a `bigint`.
 *
 * This is the only place the UI is allowed to turn money into a number,
 * and it deliberately produces an integer count of cents rather than a
 * float. Use it wherever the screen genuinely has to add or compare.
 *
 * Throws on anything that is not a decimal number, because a silent `0`
 * would show a total that is wrong rather than a screen that is broken.
 * Empty and null are 0: an absent optional amount is not an error.
 */
export const moneyToCents = (amount?: string | null): bigint => {
  if (amount === null || amount === undefined || amount.trim() === '') {
    return ZERO;
  }
  const value = amount.trim();
  if (!MONEY_PATTERN.test(value)) {
    throw new Error(`Not a decimal money string: ${JSON.stringify(amount)}`);
  }

  const negative = value.startsWith('-');
  const [whole, fraction = ''] = value.replace(/^[-+]/, '').split('.');
  // Two decimal places is the ledger's precision (NUMERIC(12,2)); pad a
  // short fraction, and truncate a long one rather than rounding it,
  // since nothing this side of the API should be inventing a cent.
  const cents =
    BigInt(whole) * HUNDRED + BigInt(fraction.padEnd(2, '0').slice(0, 2));
  return negative ? -cents : cents;
};

/** The inverse of {@link moneyToCents} — a canonical `"1250.00"`. */
export const centsToMoney = (cents: bigint): string => {
  const negative = cents < ZERO;
  const abs = negative ? -cents : cents;
  const whole = abs / HUNDRED;
  const fraction = (abs % HUNDRED).toString().padStart(2, '0');
  return `${negative ? '-' : ''}${whole}.${fraction}`;
};

/** Exact sum of money strings; null, undefined and `''` are skipped. */
export const sumMoney = (
  amounts: ReadonlyArray<string | null | undefined>
): string =>
  centsToMoney(amounts.reduce<bigint>((acc, a) => acc + moneyToCents(a), ZERO));

/** `-1`, `0` or `1`, comparing exactly. */
export const compareMoney = (a?: string | null, b?: string | null): number => {
  const left = moneyToCents(a);
  const right = moneyToCents(b);
  if (left < right) return -1;
  if (left > right) return 1;
  return 0;
};

/** True when the amount is below zero — a credit, on this ledger. */
export const isNegativeMoney = (amount?: string | null): boolean =>
  moneyToCents(amount) < ZERO;

/** The amount without its sign, still as a decimal string. */
export const absMoney = (amount?: string | null): string => {
  const cents = moneyToCents(amount);
  return centsToMoney(cents < ZERO ? -cents : cents);
};

/** Whether the aging buckets and the balance agree. */
export type AgingCheck =
  | { status: 'ok' }
  | { status: 'not_applicable' }
  | { status: 'mismatch'; bucketTotal: string; balance: string };

/**
 * The aging buckets sum to the balance, so this is a free sanity check
 * wherever a screen shows both.
 *
 * **It does not hold for a credit balance**, and that is not a bug: the
 * buckets age *open charges*, and a home in credit has none, so the
 * total is `0.00` against a negative balance. Such a home returns
 * `not_applicable` rather than a warning nobody can act on — which is
 * why this is a three-way result and not a boolean. A boolean would
 * make every home in credit look broken.
 */
export const agingSumsToBalance = (
  aging?: AgingBuckets | null,
  balance?: string | null
): AgingCheck => {
  if (!aging) return { status: 'not_applicable' };
  if (moneyToCents(balance) < ZERO) return { status: 'not_applicable' };

  const bucketTotal = sumMoney([
    aging.current,
    aging.days_1_30,
    aging.days_31_60,
    aging.days_61_90,
    aging.days_over_90,
  ]);

  if (compareMoney(bucketTotal, balance ?? '0.00') === 0) {
    return { status: 'ok' };
  }
  return { status: 'mismatch', bucketTotal, balance: balance ?? '0.00' };
};

/**
 * Render a `NaiveDate` (`"2026-08-23"`) without a timezone shift.
 *
 * **Never `new Date("2026-08-23")`.** That parses as UTC midnight, so
 * everywhere west of Greenwich renders the day before. Billing dates are
 * already resolved on the association's own calendar before they leave
 * the backend, so the only correct thing to do here is read the three
 * numbers and show them; there is no timezone left to apply.
 */
export const formatBillingDate = (date?: string | null): string => {
  if (!date) return '-';
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(date.trim());
  if (!match) return date;
  const [, year, month, day] = match;
  const local = new Date(Number(year), Number(month) - 1, Number(day));
  return isNaN(local.getTime()) ? date : local.toLocaleDateString();
};

/** Just the date part of a unix-seconds timestamp. */
export const formatEpochDate = (seconds?: number | null): string => {
  if (seconds === null || seconds === undefined) return '-';
  const d = new Date(seconds * 1000);
  return isNaN(d.getTime()) ? '-' : d.toLocaleDateString();
};

/**
 * Debit/credit rendering for a signed ledger amount.
 *
 * **Positive increases what the homeowner owes**, so a credit is
 * negative and callers colour on `isCredit`.
 */
export const signedAmountParts = (
  amount?: string | null
): { text: string; isCredit: boolean } => ({
  text: formatMoney(amount),
  isCredit: !!amount && isNegativeMoney(amount),
});

/**
 * Serialise a set of ledger entry types for the ledger's `entry_type`
 * filter, which is **comma-separated** — the backend refuses to read a
 * repeated query parameter, because `serde_urlencoded` keeps only the
 * last one and a filter that silently drops most of itself is worse
 * than no filter. Empty means "no filter", so this returns `undefined`.
 */
export const entryTypeFilter = (
  types?: ReadonlyArray<string> | null
): string | undefined =>
  types && types.length > 0 ? types.join(',') : undefined;

/**
 * Save a PDF the API rendered, using the filename it chose.
 *
 * Statements and coupons are rendered server-side so a homeowner gets a
 * real file to keep, email or hand to a lender. They come back as bytes
 * with a `content-disposition`, so the browser uses the name the
 * backend picked rather than one invented here.
 *
 * The generated client declares `Accept: application/json` for these,
 * which is wrong but harmless — the server ignores it, and the caller
 * passing `responseType: 'blob'` is what decides how axios reads the
 * body.
 */
export const savePdfResponse = (
  response: { data: unknown; headers?: unknown },
  fallbackName: string
): void => {
  const headers = (response.headers ?? {}) as Record<string, string>;
  const disposition =
    headers['content-disposition'] ?? headers['Content-Disposition'] ?? '';
  const match = /filename="?([^";]+)"?/i.exec(disposition);
  const name = match ? match[1] : fallbackName;

  const blob = response.data as Blob;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

/**
 * True when a fine still has money owing against it.
 *
 * A fine is an ordinary ledger charge now, so "outstanding" means the
 * charge is neither settled nor withdrawn.
 */
export const fineIsOutstanding = (fine?: FineReturn | null): boolean =>
  !!fine && fine.status !== 'paid' && fine.status !== 'reversed';

/**
 * Label and badge colour for a fine's status.
 *
 * Mirrors `fineStatus` in `front-porch/src/billing/enumLabels.ts`. An
 * unrecognised status title-cases rather than vanishing, so a backend
 * enum addition shows up as an unstyled option rather than a blank cell.
 */
export const fineStatusLabel = (status?: string | null): string => {
  switch (status) {
    case 'unpaid':
      return 'Unpaid';
    case 'partially_paid':
      return 'Part paid';
    case 'paid':
      return 'Paid';
    case 'reversed':
      return 'Withdrawn';
    default:
      return status
        ? status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
        : '-';
  }
};

export const fineStatusClasses = (status?: string | null): string => {
  switch (status) {
    case 'unpaid':
      return 'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100';
    case 'partially_paid':
      return 'bg-amber-100 text-amber-800 dark:bg-amber-800 dark:text-amber-100';
    case 'paid':
      return 'bg-lime-100 text-lime-800 dark:bg-lime-800 dark:text-lime-100';
    case 'reversed':
      return 'bg-zinc-100 text-zinc-800 dark:bg-zinc-800 dark:text-zinc-100';
    default:
      return 'bg-zinc-100 text-zinc-800 dark:bg-zinc-800 dark:text-zinc-100';
  }
};

/**
 * Surface the backend's message where it wrote one.
 *
 * The member-portal handlers answer with `{ "error": "..." }`; billing's
 * management handlers answer with a plain-text body. Both are written to
 * be read, so prefer either over a generic failure. Since #652 this is
 * `apiErrorMessage` under a name that says billing; it used to fall
 * through to axios's own "Request failed with status code 500".
 */
export const billingErrorMessage = (
  err: unknown,
  fallback = 'Something went wrong. Please try again.'
): string => apiErrorMessage(err, fallback);
